import React from "react";
import PageHeader from "../../common/PageHeader";
import {
  AccountBalanceWalletOutlined,
  RefreshOutlined,
  TrendingUpOutlined,
  SyncOutlined,
  InsightsOutlined,
  ContentCopyOutlined,
  EditOutlined,
  CalendarMonthOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { GlobalContext } from "../../context/GlobalContext";

const BRAND = "#6D28D9";

const PERIOD_PRESETS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "mtd", label: "Month to date" },
  { value: "custom", label: "Custom range" },
];

function toApiDate(m) {
  if (!m || !moment.isMoment(m) || !m.isValid()) return "";
  return m.format("YYYY-MM-DD");
}

function fmtCredits(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatCard({ label, value, hint, color, icon }) {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        p: 2.25,
        flex: 1,
        minWidth: 180,
        borderRadius: 2.5,
        borderColor: alpha(theme.palette.divider, 0.9),
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        {icon ? (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(color || BRAND, 0.12),
              color: color || BRAND,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {label}
          </Typography>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ mt: 0.25, color: color || "text.primary", lineHeight: 1.2 }}
          >
            {value}
          </Typography>
          {hint ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              {hint}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Card>
  );
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed" || s === "success" || s === "finished") {
    return { bg: alpha("#16A34A", 0.12), color: "#15803D" };
  }
  if (s === "failed" || s === "error") {
    return { bg: alpha("#DC2626", 0.12), color: "#B91C1C" };
  }
  if (s.includes("generat") || s === "pending" || s === "queued") {
    return { bg: alpha("#EA580C", 0.12), color: "#C2410C" };
  }
  return { bg: alpha("#64748B", 0.12), color: "#475569" };
}

export default function PoyoProfit({ lang }) {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [balance, setBalance] = React.useState(null);
  const [balanceError, setBalanceError] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [totals, setTotals] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [status, setStatus] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [backfilling, setBackfilling] = React.useState(false);
  const [dateFrom, setDateFrom] = React.useState(null);
  const [dateTo, setDateTo] = React.useState(null);
  const [periodPreset, setPeriodPreset] = React.useState("all");
  const [editRow, setEditRow] = React.useState(null);
  const [editVendor, setEditVendor] = React.useState("");
  const [editSystem, setEditSystem] = React.useState("");
  const [editTaskId, setEditTaskId] = React.useState("");
  const [savingEdit, setSavingEdit] = React.useState(false);

  const loadBalance = React.useCallback(async () => {
    const res = await hitAxios({
      path: "/api/admin/poyo_balance",
      post: false,
      admin: true,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setBalance(res.data.data);
      setBalanceError("");
    } else {
      setBalance(null);
      setBalanceError(res?.data?.msg || "Could not load provider balance");
    }
  }, [hitAxios]);

  const loadLedger = React.useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        page: String(page + 1),
        page_size: String(pageSize),
      });
      if (status) qs.set("status", status);
      const from = toApiDate(dateFrom);
      const to = toApiDate(dateTo);
      if (from) qs.set("date_from", from);
      if (to) qs.set("date_to", to);
      const res = await hitAxios({
        path: `/api/admin/poyo_profit_ledger?${qs.toString()}`,
        post: false,
        admin: true,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) {
        setRows(res.data.data?.items || []);
        setTotal(Number(res.data.data?.total) || 0);
        setTotals(res.data.data?.totals || null);
      } else {
        showSnack?.(res?.data?.msg || "Could not load ledger", false);
      }
    } finally {
      setLoading(false);
    }
  }, [hitAxios, page, pageSize, showSnack, status, dateFrom, dateTo]);

  React.useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  React.useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const refreshAll = async () => {
    await Promise.all([loadBalance(), loadLedger()]);
  };

  const applyPeriodPreset = (value) => {
    setPeriodPreset(value);
    setPage(0);
    const now = moment();
    if (value === "all") {
      setDateFrom(null);
      setDateTo(null);
      return;
    }
    if (value === "today") {
      setDateFrom(now.clone().startOf("day"));
      setDateTo(now.clone().endOf("day"));
      return;
    }
    if (value === "7d") {
      setDateFrom(now.clone().subtract(6, "days").startOf("day"));
      setDateTo(now.clone().endOf("day"));
      return;
    }
    if (value === "30d") {
      setDateFrom(now.clone().subtract(29, "days").startOf("day"));
      setDateTo(now.clone().endOf("day"));
      return;
    }
    if (value === "mtd") {
      setDateFrom(now.clone().startOf("month"));
      setDateTo(now.clone().endOf("day"));
    }
  };

  const handleDateFromChange = (value) => {
    setPeriodPreset("custom");
    setPage(0);
    setDateFrom(value);
    if (value && dateTo && moment(value).isAfter(dateTo, "day")) {
      setDateTo(value);
    }
  };

  const handleDateToChange = (value) => {
    setPeriodPreset("custom");
    setPage(0);
    if (value && dateFrom && moment(value).isBefore(dateFrom, "day")) {
      setDateFrom(value);
    }
    setDateTo(value);
  };

  const clearDateRange = () => {
    setPeriodPreset("all");
    setPage(0);
    setDateFrom(null);
    setDateTo(null);
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const res = await hitAxios({
        path: "/api/admin/poyo_profit_backfill",
        post: true,
        admin: true,
        obj: { limit: 50 },
        showLoading: false,
      });
      if (res?.data?.success) {
        const d = res.data.data || {};
        showSnack?.(
          `Backfill: ${d.updated || 0} updated, ${d.skipped || 0} skipped (scanned ${d.scanned || 0})`,
          true,
        );
        await loadLedger();
      } else {
        showSnack?.(res?.data?.msg || "Backfill failed", false);
      }
    } finally {
      setBackfilling(false);
    }
  };

  const copyTaskId = async (taskId) => {
    if (!taskId) return;
    try {
      await navigator.clipboard.writeText(String(taskId));
      showSnack?.("Task ID copied", true);
    } catch {
      showSnack?.("Could not copy task ID", false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditVendor(
      row.poyo_credits != null && Number.isFinite(Number(row.poyo_credits))
        ? String(row.poyo_credits)
        : "",
    );
    setEditSystem(
      row.system_credits != null && Number.isFinite(Number(row.system_credits))
        ? String(row.system_credits)
        : "",
    );
    setEditTaskId(row.task_id ? String(row.task_id) : "");
  };

  const closeEdit = () => {
    if (savingEdit) return;
    setEditRow(null);
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const vendorRaw = String(editVendor).trim();
    const systemRaw = String(editSystem).trim();
    const taskRaw = String(editTaskId).trim();
    const payload = {
      id: editRow.id,
      source: editRow.source || "video",
      vendor_credits: vendorRaw === "" ? null : Number(vendorRaw),
      system_credits: systemRaw === "" ? null : Number(systemRaw),
      task_id: taskRaw === "" ? null : taskRaw,
    };
    if (payload.vendor_credits != null && !Number.isFinite(payload.vendor_credits)) {
      showSnack?.("Vendor cost must be a valid number", false);
      return;
    }
    if (payload.system_credits != null && !Number.isFinite(payload.system_credits)) {
      showSnack?.("System charge must be a valid number", false);
      return;
    }
    setSavingEdit(true);
    try {
      const res = await hitAxios({
        path: "/api/admin/poyo_profit_update_costs",
        post: true,
        admin: true,
        obj: payload,
        showLoading: false,
      });
      if (res?.data?.success) {
        showSnack?.(res.data.msg || "Updated", true);
        setEditRow(null);
        await loadLedger();
      } else {
        showSnack?.(res?.data?.msg || "Update failed", false);
      }
    } finally {
      setSavingEdit(false);
    }
  };

  const rangeHint =
    dateFrom || dateTo
      ? `Completed jobs · ${toApiDate(dateFrom) || "…"} → ${toApiDate(dateTo) || "…"}`
      : "Completed jobs (all time)";

  return (
    <Box>
      <PageHeader
        title={lang?.profitLossTitle || "Profit/Loss"}
        subtitle={
          lang?.profitLossSub ||
          "Live API key balance and per-generation margin (system charge vs vendor cost)."
        }
        icon={InsightsOutlined}
        primaryAction={
          <Stack direction="row" spacing={1}>
            <Tooltip
              title="Fetches vendor cost from the generation provider for completed jobs that have a task ID but no recorded vendor credits yet."
            >
              <span>
                <LoadingButton
                  loading={backfilling}
                  variant="outlined"
                  startIcon={<SyncOutlined />}
                  onClick={backfill}
                  sx={{ fontWeight: 700, textTransform: "none" }}
                >
                  {lang?.profitLossBackfill || "Backfill costs"}
                </LoadingButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={refreshAll} disabled={loading}>
                <RefreshOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <StatCard
          icon={<AccountBalanceWalletOutlined fontSize="small" />}
          label={lang?.providerBalance || "API balance"}
          value={
            balance
              ? fmtCredits(balance.credits_amount)
              : balanceError
                ? "—"
                : "…"
          }
          hint={
            balance?.email
              ? `${balance.email}${balance.fetched_at ? ` · ${fmtDate(balance.fetched_at)}` : ""}`
              : balanceError || "Vendor credits on API key"
          }
          color={theme.palette.primary.main}
        />
        <StatCard
          icon={<TrendingUpOutlined fontSize="small" />}
          label={lang?.systemCharged || "System charged"}
          value={fmtCredits(totals?.system_credits)}
          hint={rangeHint}
        />
        <StatCard
          label={lang?.vendorCost || "Vendor cost"}
          value={fmtCredits(totals?.poyo_credits)}
          hint="From provider task usage (or manual edit)"
          color="#EA580C"
        />
        <StatCard
          label={lang?.profitLoss || "Profit / Loss"}
          value={fmtCredits(totals?.profit_credits)}
          hint="System charged − vendor cost"
          color={
            Number(totals?.profit_credits) >= 0
              ? theme.palette.success.main
              : theme.palette.error.main
          }
        />
      </Stack>

      <Card
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          overflow: "hidden",
          borderColor: alpha(theme.palette.divider, 0.9),
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.25,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(BRAND, 0.12),
                  color: BRAND,
                }}
              >
                <TrendingUpOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography fontWeight={800}>
                  {lang?.profitLossLedger || "Generation ledger"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {total.toLocaleString()} generations
                </Typography>
              </Box>
            </Stack>
            <Select
              size="small"
              value={status}
              displayEmpty
              onChange={(e) => {
                setPage(0);
                setStatus(e.target.value);
              }}
              sx={{ minWidth: 160, bgcolor: "background.paper" }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="segment_ready">Segment ready</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="generating">Generating</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </Stack>

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              <CalendarMonthOutlined fontSize="small" color="action" />
              <Select
                size="small"
                value={periodPreset}
                onChange={(e) => applyPeriodPreset(e.target.value)}
                sx={{ minWidth: 150, bgcolor: "background.paper" }}
                displayEmpty
              >
                {PERIOD_PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
              <DatePicker
                label={lang?.fromDate || "From"}
                value={dateFrom}
                onChange={handleDateFromChange}
                maxDate={dateTo || undefined}
                format="DD MMM YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: { xs: "100%", sm: 168 }, bgcolor: "background.paper" },
                  },
                  field: { clearable: true },
                }}
              />
              <DatePicker
                label={lang?.toDate || "To"}
                value={dateTo}
                onChange={handleDateToChange}
                minDate={dateFrom || undefined}
                maxDate={moment()}
                format="DD MMM YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: { xs: "100%", sm: 168 }, bgcolor: "background.paper" },
                  },
                  field: { clearable: true },
                }}
              />
              <Button
                size="small"
                variant="text"
                onClick={clearDateRange}
                disabled={!dateFrom && !dateTo}
              >
                {lang?.clear || "Clear"}
              </Button>
            </Stack>
          </LocalizationProvider>
        </Stack>

        <TableContainer sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "ID",
                  "Date / time",
                  "User",
                  "Model",
                  "Duration",
                  "Status",
                  "Vendor",
                  "System",
                  "P/L",
                  "Task",
                  "",
                ].map((h) => (
                  <TableCell
                    key={h || "actions"}
                    align={["Vendor", "System", "P/L"].includes(h) ? "right" : "left"}
                    sx={{
                      fontWeight: 800,
                      fontSize: 12,
                      letterSpacing: 0.2,
                      textTransform: "uppercase",
                      color: "text.secondary",
                      bgcolor: "background.paper",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 4, textAlign: "center" }}
                    >
                      {loading ? "Loading…" : "No generations in this range."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => {
                  const profit = row.profit_credits;
                  const tone = statusTone(row.status);
                  return (
                    <TableRow
                      key={row.record_key || `${row.source || "video"}:${row.id}`}
                      hover
                      sx={{
                        bgcolor: idx % 2 === 0 ? "transparent" : alpha(theme.palette.text.primary, 0.015),
                        "& td": { borderColor: alpha(theme.palette.divider, 0.6), py: 1.25 },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} color="text.secondary">
                          {row.source === "image" ? "IMG" : "VID"} #{row.id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2">{fmtDate(row.created_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {row.user_name || row.user_email || row.uid}
                        </Typography>
                        {row.user_name && row.user_email ? (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {row.user_email}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 180 }}>
                          {row.ui_model}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ maxWidth: 200 }}>
                          {row.api_model}
                          {row.resolution ? ` · ${row.resolution}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.duration_sec != null ? `${row.duration_sec}s` : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status}
                          sx={{
                            fontWeight: 700,
                            bgcolor: tone.bg,
                            color: tone.color,
                            border: "none",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {fmtCredits(row.poyo_credits)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {fmtCredits(row.system_credits)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={fmtCredits(profit)}
                          sx={{
                            fontWeight: 800,
                            minWidth: 64,
                            justifyContent: "center",
                            bgcolor:
                              profit == null
                                ? alpha("#64748B", 0.1)
                                : profit >= 0
                                  ? alpha("#16A34A", 0.12)
                                  : alpha("#DC2626", 0.12),
                            color:
                              profit == null
                                ? "text.secondary"
                                : profit >= 0
                                  ? "#15803D"
                                  : "#B91C1C",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {row.task_id ? (
                          <Stack direction="row" alignItems="center" spacing={0.25}>
                            <Typography
                              variant="caption"
                              title={row.task_id}
                              sx={{
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                maxWidth: 110,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                color: "text.secondary",
                              }}
                            >
                              {row.task_id}
                            </Typography>
                            <Tooltip title="Copy task ID">
                              <IconButton
                                size="small"
                                onClick={() => copyTaskId(row.task_id)}
                                aria-label="Copy task ID"
                              >
                                <ContentCopyOutlined sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit vendor / system costs">
                          <IconButton size="small" onClick={() => openEdit(row)} aria-label="Edit costs">
                            <EditOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <Dialog open={Boolean(editRow)} onClose={closeEdit} fullWidth maxWidth="xs">
        <DialogTitle>Edit generation · #{editRow?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update provider task ID, vendor cost, and system charge. Leave a field blank to clear it.
          </Typography>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Task ID"
              value={editTaskId}
              onChange={(e) => setEditTaskId(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: editTaskId ? (
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => copyTaskId(editTaskId)}
                    aria-label="Copy task ID"
                  >
                    <ContentCopyOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null,
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 13,
                },
              }}
            />
            <TextField
              label="Vendor credits"
              value={editVendor}
              onChange={(e) => setEditVendor(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: "any" }}
              fullWidth
              size="small"
            />
            <TextField
              label="System credits"
              value={editSystem}
              onChange={(e) => setEditSystem(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              size="small"
              helperText="Stored as a whole number"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={savingEdit}>
            Cancel
          </Button>
          <LoadingButton loading={savingEdit} variant="contained" onClick={saveEdit}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
